import { Link } from 'react-router-dom'
import { useState, useEffect } from 'react'
import VideoPlayer from '../components/VideoPlayer'

// Intersection Observer hook for lazy loading animations
const useInView = (threshold = 0.1) => {
  const [ref, setRef] = useState(null)
  const [inView, setInView] = useState(false)

  useEffect(() => {
    if (!ref) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true)
          observer.disconnect()
        }
      },
      { threshold }
    )

    observer.observe(ref)
    return () => observer.disconnect()
  }, [ref, threshold])

  return [setRef, inView]
}

// Animation component that uses CSS transitions instead of framer-motion
// Modified to not block initial paint on mobile
const AnimatedDiv = ({ children, delay = 0, className = '', priority = false, ...props }) => {
  const [ref, inView] = useInView()
  const [mounted, setMounted] = useState(false)
  
  useEffect(() => {
    setMounted(true)
  }, [])
  
  // For priority content (above fold), show immediately without animation
  if (priority || !mounted) {
    return (
      <div className={className} {...props}>
        {children}
      </div>
    )
  }
  
  return (
    <div
      ref={ref}
      className={`transition-all duration-600 ${
        inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'
      } ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
      {...props}
    >
      {children}
    </div>
  )
}

function Home() {
  return (
    <div className="pt-20">
      {/* Hero Section - Critical content, no JS-dependent animations */}
      <section className="relative bg-gradient-to-br from-gray-50 to-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <div className="text-center hero-content">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Turn Old, Dead Leads Into{' '}
              <span className="bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
                Instant Cash Flow
              </span>
              —On Autopilot.
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Lumeryx re-ignites the cold, forgotten prospects hiding in your CRM with AI-driven SMS conversations—with a success-based model that guarantees results.
            </p>
            <Link
              to="/case-study-1-register"
              reloadDocument
              className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-primary-600 to-secondary-600 text-white rounded-full font-semibold text-lg hover:shadow-xl transition-shadow duration-200 hover:scale-105"
            >
              Watch Free Case Study
              <svg className="ml-2 w-5 h-5" width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </Link>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
      </section>

      {/* Social Proof Bar */}
      <section className="bg-primary-50 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedDiv priority className="flex flex-wrap justify-center items-center gap-8 text-sm text-gray-700">
            <div className="flex items-center space-x-2">
              <span className="text-accent-600 font-semibold">✓</span>
              <span>"Added $72k in 14 days"</span>
            </div>
            <div className="hidden md:block w-px h-6 bg-gray-300"></div>
            <div className="flex items-center space-x-2">
              <span className="text-accent-600 font-semibold">✓</span>
              <span>"37% of 'dead' leads booked calls"</span>
            </div>
            <div className="hidden md:block w-px h-6 bg-gray-300"></div>
            <div className="flex items-center space-x-2">
              <span className="text-accent-600 font-semibold">✓</span>
              <span>"3x month over month profit"</span>
            </div>
          </AnimatedDiv>
        </div>
      </section>

      {/* Why Your Database Is a Goldmine */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedDiv className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Your Database Is a{' '}
              <span className="bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
                Goldmine
              </span>
            </h2>
          </AnimatedDiv>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                stat: '60-70%',
                title: 'of inquiries go cold',
                desc: 'after just two touches'
              },
              {
                stat: '$0 vs $50+',
                title: 'per new lead',
                desc: 'cost comparison'
              },
              {
                stat: '1-to-1',
                title: 'conversations at scale',
                desc: 'with AI automation'
              }
            ].map((item, index) => (
              <AnimatedDiv
                key={index}
                delay={index * 100}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-200"
              >
                <div className="text-4xl font-bold text-primary-600 mb-2">{item.stat}</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.desc}</p>
              </AnimatedDiv>
            ))}
          </div>
        </div>
      </section>

      {/* How Lumeryx Works */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedDiv className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              How Lumeryx Works
            </h2>
            <p className="text-xl text-gray-600">Three simple steps to revenue reactivation</p>
          </AnimatedDiv>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: '1',
                title: 'Upload',
                desc: 'You provide us your old, dead contacts',
                icon: '📤'
              },
              {
                step: '2',
                title: 'Engage',
                desc: 'Our AI texts each lead using proven sales techniques',
                icon: '💬'
              },
              {
                step: '3',
                title: 'Book',
                desc: 'Qualified prospects auto-book on your calendar',
                icon: '📅'
              }
            ].map((item, index) => (
              <AnimatedDiv
                key={index}
                delay={index * 100}
                className="relative"
              >
                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-200 h-full">
                  <div className="text-4xl mb-4">{item.icon}</div>
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-primary-600 to-secondary-600 text-white rounded-full font-bold mb-4">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.desc}</p>
                </div>
                {index < 2 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <svg className="w-8 h-8 text-gray-400" width="32" height="32" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                )}
              </AnimatedDiv>
            ))}
          </div>
        </div>
      </section>

      {/* Success-Based Partnership Model */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedDiv className="bg-gradient-to-br from-primary-600 to-secondary-700 rounded-3xl p-12 text-white text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              "A True Partnership: Your Success Is Our Success"
            </h2>
            <div className="grid md:grid-cols-3 gap-8 mb-8">
              <div>
                <div className="text-5xl font-bold mb-2">100%</div>
                <p className="text-primary-100">Refundable investment</p>
              </div>
              <div>
                <div className="text-5xl font-bold mb-2">Shared</div>
                <p className="text-primary-100">Success-based revenue model</p>
              </div>
              <div>
                <div className="text-5xl font-bold mb-2">Risk-Free</div>
                <p className="text-primary-100">Full refund if we don't deliver</p>
              </div>
            </div>
            <Link
              to="/case-study-1-register"
              reloadDocument
              className="inline-flex items-center px-8 py-4 bg-white text-primary-700 rounded-full font-semibold text-lg hover:shadow-xl transition-all duration-200 hover:scale-105"
            >
              Watch Free Case Study
            </Link>
          </AnimatedDiv>
        </div>
      </section>

      {/* Live Demo Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedDiv className="text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              See It In Action
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              Watch how a cold lead becomes a booked appointment in minutes
            </p>
            <div className="bg-white rounded-2xl shadow-xl p-8 max-w-4xl mx-auto">
              <div className="aspect-video rounded-lg overflow-hidden">
                <VideoPlayer src="/Lumeryx_home.mp4" />
              </div>
            </div>
          </AnimatedDiv>
        </div>
      </section>

      {/* Features Snapshot */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedDiv className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Everything You Need to{' '}
              <span className="bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
                Reactivate Revenue
              </span>
            </h2>
          </AnimatedDiv>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: '🤖',
                title: 'Smart SMS Sequences',
                desc: 'Using proven sales techniques'
              },
              {
                icon: '✅',
                title: 'Built-in Compliance',
                desc: 'Opt-out handling included'
              },
              {
                icon: '📊',
                title: 'Real-time Dashboard',
                desc: 'Track calls & closed deals'
              },
              {
                icon: '🔒',
                title: 'SOC-2 Ready',
                desc: 'Enterprise-grade security'
              }
            ].map((feature, index) => (
              <AnimatedDiv
                key={index}
                delay={index * 50}
                className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-200"
              >
                <div className="text-3xl mb-3">{feature.icon}</div>
                <h3 className="font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-sm text-gray-600">{feature.desc}</p>
              </AnimatedDiv>
            ))}
          </div>
        </div>
      </section>

      {/* Case Study */}
      <section className="py-20 bg-gradient-to-br from-primary-50 to-secondary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedDiv className="bg-white rounded-3xl shadow-xl p-8 md:p-12">
            <div className="text-center mb-8">
              <p className="text-primary-600 font-semibold mb-2">CASE STUDY</p>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                How NV Roofing Pulled $129,500 From 1,247 'Dead' Leads in 21 Days
              </h2>
            </div>
            <div className="grid md:grid-cols-3 gap-8 text-center">
              <div>
                <div className="text-4xl font-bold text-primary-600 mb-2">48%</div>
                <p className="text-gray-600">Reply Rate</p>
              </div>
              <div>
                <div className="text-4xl font-bold text-primary-600 mb-2">46</div>
                <p className="text-gray-600">Calls Booked</p>
              </div>
              <div>
                <div className="text-4xl font-bold text-primary-600 mb-2">$129.5k</div>
                <p className="text-gray-600">Cash Collected</p>
              </div>
            </div>
          </AnimatedDiv>
        </div>
      </section>

      {/* FAQ/Objection Crushers */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedDiv className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Common Questions
            </h2>
          </AnimatedDiv>
          <div className="space-y-6">
            {[
              {
                q: 'Will this annoy my contacts?',
                a: 'Our AI uses natural, human-tone messaging with a strict 2-message cap and instant STOP keyword recognition.'
              },
              {
                q: 'How do you track sales?',
                a: 'Direct webhook integration with your CRM or Stripe ensures accurate, real-time tracking.'
              },
              {
                q: 'Do I lose control of my data?',
                a: 'Never. Your data remains yours with 256-bit encryption and full export capabilities anytime.'
              }
            ].map((item, index) => (
              <AnimatedDiv
                key={index}
                delay={index * 100}
                className="bg-white rounded-xl p-6 shadow-lg"
              >
                <h3 className="font-semibold text-lg text-gray-900 mb-2">{item.q}</h3>
                <p className="text-gray-600">{item.a}</p>
              </AnimatedDiv>
            ))}
          </div>
        </div>
      </section>

      {/* Risk Reversal Banner */}
      <section className="py-16 bg-accent-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <AnimatedDiv>
            <div className="inline-flex items-center justify-center w-16 h-16 bg-accent-500 text-white rounded-full mb-4">
              <svg className="w-8 h-8" width="32" height="32" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              Your Investment Is Protected—Full Refund If We Don't Deliver Results
            </h2>
            <p className="text-lg text-gray-600">
              We're so confident in our system, we guarantee your investment is fully refundable.
            </p>
          </AnimatedDiv>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <AnimatedDiv>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Ready to See the AI in Action?
            </h2>
            <Link
              to="/case-study-1-register"
              reloadDocument
              className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-primary-600 to-secondary-600 text-white rounded-full font-semibold text-lg hover:shadow-xl transition-all duration-200 hover:scale-105"
            >
              Watch Free Case Study
              <svg className="ml-2 w-5 h-5" width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </Link>
            <p className="text-sm text-gray-500 mt-4">Unlock the full breakdown instantly</p>
          </AnimatedDiv>
        </div>
      </section>
    </div>
  )
}

export default Home
